/**
 * The furniture around a Quick Bytes card —
 * ROUTE_INVENTORY §2.12 #17, #19, #20, #22 and #23.
 *
 * Extracted from `QuickBytesCarouselModal`, which was 448 lines and reachable
 * only through a modal, an infinite query and a deck cursor. Each piece here is
 * pure props, and each is a place the design makes a specific claim that is
 * easy to break and hard to notice.
 */

import { CalendarCheck, ChevronLeft, ChevronRight, Tag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import type { HighlightsFeedCarousel } from '@/lib/api-client';
import { InlineMarkdown } from '@/components/core/markdown/markdown';

/** Fixed square design canvas each card lays out against; scaled to fit. */
export const CARD_DESIGN_PX = 800;

/** How many domain tags the header will show before it stops. */
export const MAX_HEADER_TAGS = 4;

/**
 * §2.12 #22 — concept name, source lesson, due pill, up to four tags.
 *
 * The tag cap is the only real decision. The header sits above a square card in
 * a 90vh modal, and a concept carrying nine domain tags would wrap the meta
 * strip onto three lines and shove the card out of view. Four is what fits on
 * one line at the narrowest modal width.
 */
export function CarouselHeaderMeta({
  carousel,
}: {
  carousel: HighlightsFeedCarousel;
}) {
  return (
    <div className='flex-shrink-0 px-6 pb-3 pr-28 pt-10'>
      <h2 className='text-xl font-semibold text-foreground'>
        <InlineMarkdown>{carousel.concept_name}</InlineMarkdown>
      </h2>
      <div className='mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1'>
        {carousel.source_lesson_title && (
          <span className='text-xs text-muted-foreground'>
            from{' '}
            <span className='font-medium text-foreground/70'>
              {carousel.source_lesson_title}
            </span>
          </span>
        )}
        {carousel.is_due_today && (
          <span className='inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'>
            <CalendarCheck className='h-3 w-3' aria-hidden />
            Due today
          </span>
        )}
        {carousel.domain_tags.slice(0, MAX_HEADER_TAGS).map(tag => (
          <span
            key={tag}
            className='dark:tint-brand inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700'
          >
            <Tag className='h-3 w-3' aria-hidden />
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * §2.12 #17 — invisible 45% tap halves with a dead 10% centre.
 *
 * The whole card body is the navigation surface, which is what makes this read
 * like a story reader rather than a slideshow. The dead centre is the part
 * worth protecting: without it, every attempt to point at something in the
 * middle of a card advances it.
 *
 * They are `role='button'` with `tabIndex` and key handlers rather than bare
 * divs — invisible does not mean unreachable, and these are the primary
 * navigation on touch.
 */
export function CarouselTapZones({
  onPrev,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}) {
  const keyActivate = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        aria-label='Previous card'
        onClick={onPrev}
        onKeyDown={keyActivate(onPrev)}
        className='absolute inset-y-0 left-0 z-10 w-[45%] outline-none focus-visible:ring-0 focus-visible:ring-offset-0'
      />
      <div
        role='button'
        tabIndex={0}
        aria-label='Next card'
        onClick={onNext}
        onKeyDown={keyActivate(onNext)}
        className='absolute inset-y-0 right-0 z-10 w-[45%] outline-none focus-visible:ring-0 focus-visible:ring-offset-0'
      />
    </>
  );
}

/**
 * §2.12 #19 — chevrons that appear on hover, desktop only.
 *
 * Quiet by default so the card is the only thing on screen, and `md:` only
 * because on touch the tap halves and swipe already are the surface. The
 * `focus-visible:opacity-100` is the important half: a keyboard user gets no
 * hover, so without it these would be focusable and permanently invisible.
 */
export function CarouselChevrons({
  onPrev,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}) {
  const base =
    'absolute top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground/70 opacity-0 shadow-sm backdrop-blur transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 group-hover/modal:opacity-100 dark:focus-visible:ring-primary/40 md:inline-flex';

  return (
    <>
      <button
        type='button'
        onClick={onPrev}
        aria-label='Previous card'
        className={cn(base, 'left-4')}
      >
        <ChevronLeft className='h-5 w-5' aria-hidden />
      </button>
      <button
        type='button'
        onClick={onNext}
        aria-label='Next card'
        className={cn(base, 'right-4')}
      >
        <ChevronRight className='h-5 w-5' aria-hidden />
      </button>
    </>
  );
}

/**
 * §2.12 #23 — one dot per card, the active one widened.
 *
 * Width rather than colour alone carries the position, so it survives both
 * themes and a colour-blind reader. Decorative: the same information is in the
 * card itself and in the tap zones' labels, and a screen reader announcing
 * "bullet bullet bullet" would be noise.
 */
export function CarouselDots({
  count,
  activeIndex,
}: {
  count: number;
  activeIndex: number;
}) {
  return (
    <div
      className='flex flex-shrink-0 items-center justify-center gap-1.5 pb-4 pt-2'
      aria-hidden
    >
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          data-active={i === activeIndex ? 'true' : undefined}
          className={cn(
            'h-1.5 rounded-full transition-all duration-300',
            i === activeIndex
              ? 'w-4 bg-primary'
              : 'w-1.5 bg-muted-foreground/30'
          )}
        />
      ))}
    </div>
  );
}

/**
 * §2.12 #20 — an 800px design canvas scaled into the measured area.
 *
 * Every highlight template lays out against a fixed square, so scaling the
 * whole thing like an image is what keeps a card's proportions identical in a
 * 320px thumbnail and a 900px modal. The alternative — responsive type and
 * spacing inside each template — would mean 20+ templates each getting it
 * subtly wrong.
 *
 * `side` comes from a `ResizeObserver` on the parent and is 0 until it
 * measures, which is why this renders nothing rather than a zero-scaled card.
 */
export function ScaledCardCanvas({
  side,
  children,
  resetKey,
}: {
  side: number;
  children: React.ReactNode;
  /** Remounts the inner canvas, restarting any entry animation on card change. */
  resetKey: string;
}) {
  const phone = useMediaQuery('(max-width: 767.98px)');
  // Below `md` the canvas is the card's own size, unscaled. The templates'
  // type is responsive (`text-xs md:text-sm`, `text-xl md:text-2xl`): below
  // `md` they already pick their phone sizes, and then drawing on the 800px
  // canvas shrank them a second time - a 340px card is a 0.42 scale, which
  // took the 20px title to about 8px.
  const design = phone ? side : CARD_DESIGN_PX;
  if (side <= 0) return null;

  return (
    <div
      style={{ width: side, height: side }}
      className='relative overflow-hidden rounded-xl'
      data-card-canvas
      data-card-side={side}
    >
      <div
        key={resetKey}
        style={{
          width: design,
          height: design,
          transform: `scale(${side / design})`,
        }}
        className='origin-top-left'
      >
        {children}
      </div>
    </div>
  );
}
